/**
 * Change message → TanStack Query keys (T14-08). Convention for query keys of live data:
 *
 *   [root]                     everything of the area          entityKeys("orders").all
 *   [root, "list", ...params]  lists / tables / counters        entityKeys("orders").list(filters)
 *   [root, "detail", id]       one record                       entityKeys("orders").detail(id)
 *
 * A message invalidates `[root, "list"]` (every list of the area — prefix match) and
 * `[root, "detail", id]` of *that* record only, plus the details named in `hint` (`orderId` of a
 * payment …). Apps add / replace rules per entity with {@link createQueryKeyMap}.
 */
import type { ChangeMessage } from "../sse";
import { CMS_TOPIC } from "../topics";

export type QueryKey = readonly unknown[];

export interface EntityKeys {
  all: readonly [string];
  lists: readonly [string, "list"];
  list: (...params: readonly unknown[]) => readonly unknown[];
  details: readonly [string, "detail"];
  detail: (id: string) => readonly [string, "detail", string];
}

export function entityKeys(root: string): EntityKeys {
  return {
    all: [root],
    lists: [root, "list"],
    list: (...params) => [root, "list", ...params],
    details: [root, "detail"],
    detail: (id) => [root, "detail", id],
  };
}

/**
 * entity (hub mapping `app/mapping/*.yaml`) → key root, for the entities whose root is not the
 * default {@link rootOf} rule (`consignment_item` → `consignment-items`: kebab-case + "s"). Only
 * the exceptions ship (bundle budget, T14-07); `test/entity-roots.test.ts` pins the root of every
 * entity the hub can send, so a new mapping entity still fails the test until its root is chosen.
 */
export const ENTITY_ROOTS: Readonly<Record<string, string>> = {
  order_return: "returns",
  refund: "payments",
  payment_alert: "payments",
  inventory_item: "inventory",
  oversell_conflict: "conflicts",
  section_manifest: "themes",
  cms_content: "cms",
  media_asset: "media",
  buylist_intake: "buylist",
  store_credit: "store-credit",
  dsar_request: "data-requests",
  staff: "staff",
  audit_entry: "audit",
  inbox_item: "inbox",
  taxfree_record: "taxfree",
  taxfree_refund: "taxfree",
  ledger_discrepancy: "ledger",
  category: "categories",
  attribute_definition: "attributes",
  media_library: "media-library",
  consignment_intake: "intakes",
  kobutsu_ledger: "kobutsu",
  security_setting: "security",
  inventory_level: "inventory",
  stock_transfer: "transfers",
  stock_count: "counts",
  stock_receipt: "receipts",
  shipping_setting: "shipping",
  data_export: "exports",
  reconciliation: "reconciliation",
  taxfree_export: "taxfree",
  export_evidence: "export-evidence",
  staff_preferences: "staff-preferences", // T01-17
  store_settings: "store-settings", // T01-16
};

/** Key root of an entity: {@link ENTITY_ROOTS} (+ app `roots`), else kebab-case + "s". */
export function rootOf(
  entity: string,
  roots: Readonly<Record<string, string>> = ENTITY_ROOTS,
): string {
  return roots[entity] ?? `${entity.replaceAll("_", "-")}s`;
}

/** `hint` ids → the detail they also refresh. */
export const HINT_ROOTS: Readonly<Record<string, string>> = {
  orderId: "orders",
  paymentId: "payments",
  productId: "products",
  variantId: "variants",
  unitId: "units",
  consignorId: "consignors",
  consignmentItemId: "consignment-items",
  shiftId: "shifts",
  customerId: "customers",
  returnId: "returns",
};

/** Entity of a message — `public:*` messages carry none: it follows from the topic. */
export function entityOf(message: ChangeMessage): string | null {
  if (message.entity) {
    return message.entity;
  }
  if (message.topic.startsWith("public:variant:")) {
    return "variant";
  }
  if (message.topic.startsWith("public:collection:")) {
    return "collection";
  }
  return message.topic === CMS_TOPIC ? "cms_content" : null;
}

export type KeyRule = (
  message: ChangeMessage,
  defaults: readonly QueryKey[],
) => readonly QueryKey[];
export type KeyMap = (message: ChangeMessage) => readonly QueryKey[];

export interface KeyMapOptions {
  /** More / other entity → root (merged over {@link ENTITY_ROOTS}). */
  roots?: Readonly<Record<string, string>>;
  /** More / other hint id → root (merged over {@link HINT_ROOTS}). */
  hints?: Readonly<Record<string, string>>;
  /** Per entity: replace the keys (gets the defaults, may return them plus more). */
  rules?: Readonly<Record<string, KeyRule>>;
  /** Keys added for every message (e.g. a dashboard counter). */
  extra?: (message: ChangeMessage) => readonly QueryKey[];
}

/** Default + app rules → a function from message to the keys it invalidates. */
export function createQueryKeyMap(options: KeyMapOptions = {}): KeyMap {
  const roots = { ...ENTITY_ROOTS, ...options.roots };
  const hints = { ...HINT_ROOTS, ...options.hints };
  return (message) => {
    const entity = entityOf(message);
    const keys: QueryKey[] = [];
    if (entity) {
      const root = rootOf(entity, roots);
      const own = entityKeys(root);
      keys.push(own.lists, own.detail(message.id));
      for (const [name, value] of Object.entries(message.hint ?? {})) {
        const hintRoot = hints[name];
        if (hintRoot && typeof value === "string" && !(hintRoot === root && value === message.id)) {
          keys.push(entityKeys(hintRoot).detail(value));
        }
      }
      const rule = options.rules?.[entity];
      if (rule) {
        keys.splice(0, keys.length, ...rule(message, [...keys]));
      }
    }
    keys.push(...(options.extra?.(message) ?? []));
    return keys;
  };
}

/** `true` when `key` follows the convention (`[root, "list" | "detail", …]`). */
export function isConventionalKey(key: QueryKey): boolean {
  return typeof key[0] === "string" && (key[1] === "list" || key[1] === "detail");
}
